import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_URL, GLOBAL_PREFIX } from "../../../styles/constants";
import useLocaleQueryFilter from "../../utils/useLocaleQueryFilter";
import { ProductCardType, ResultsResponse } from "../queries/useProducts";
import useJwt from "../../utils/useJwt";

export type ToggleFavoriteInput = {
  jwt: string;
  productId: number;
  action: "add" | "remove";
};

export default function useToggleFavorite() {
  const queryClient = useQueryClient();
  const jwt = useJwt();
  const filter = useLocaleQueryFilter({ jwt });

  return useMutation(
    (input: ToggleFavoriteInput) => {
      const jsonrpc = {
        jsonrpc: "2.0",
        ns: GLOBAL_PREFIX,
        method: "favorites/toggle",
        params: input,
        id: `${Date.now()}`,
      };
      return axios.post(`${BASE_URL}/favorites/toggle`, jsonrpc, {
        headers: { "x-winamax-public-endpoint-version": 2 },
      });
    },
    {
      onMutate: async (input: ToggleFavoriteInput) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(["favorites"]);

        const previousFavoritesIds = queryClient.getQueryData<number[]>([
          "favorites",
          filter,
        ]);

        const previousFavoriteProducts = queryClient.getQueryData<
          ResultsResponse<ProductCardType[]>
        >(["favorites/products", filter]);

        // in case of add no optimistic update is happening since it doesn't need instant response
        // instead we're gonna refetch the query in background
        // in case of remove optimistic update triggers since we need instant feedback on favorite screen
        if (previousFavoriteProducts) {
          const newFavorites =
            input.action === "add"
              ? previousFavoriteProducts
              : {
                  total: previousFavoriteProducts.total - 1,
                  results: [
                    ...previousFavoriteProducts.results.filter(
                      (item) => item.id !== input.productId
                    ),
                  ],
                };

          queryClient.setQueryData<ResultsResponse<ProductCardType[]>>(
            ["favorites/products", filter],
            newFavorites
          );
        }
        // Optimistically update to the new value
        if (previousFavoritesIds) {
          const newFavorites =
            input.action === "add"
              ? [...previousFavoritesIds, input.productId]
              : [
                  ...previousFavoritesIds.filter(
                    (id) => id !== input.productId
                  ),
                ];

          queryClient.setQueryData<number[]>(
            ["favorites", filter],
            newFavorites
          );
        }

        return { previousFavoritesIds, previousFavoriteProducts };
      },
      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, variables, context) => {
        if (context?.previousFavoritesIds) {
          queryClient.setQueryData<number[]>(
            ["favorites", filter],
            context.previousFavoritesIds
          );
        }
        if (context?.previousFavoriteProducts) {
          queryClient.setQueryData<ResultsResponse<ProductCardType[]>>(
            ["favorites/products", filter],
            context.previousFavoriteProducts
          );
        }
      },
      // Always refetch after error or success:
      onSettled: (data, err, variables) => {
        queryClient.invalidateQueries(["favorites", filter]);
        queryClient.refetchQueries(["favorites/products", filter]);
      },
    }
  );
}
